import { z } from 'zod'

const TimestampSchema = z.iso.datetime({ offset: true })
const SnakeCaseCodeSchema = z.string().regex(/^[a-z][a-z0-9_]*$/)

// Response kinds stay open because producers register new kinds server-side;
// only the kinds this UI can enqueue are closed.
export const EnqueueableOperationKindSchema = z.enum(['wildberries_publish', 'ymarket_publish', 'ozon_publish'])

// Mirrors ItemReasonCode and ItemWarningCode in forge/storefront_publishing/domain.py.
export const ItemReasonCodeSchema = z.enum(['image_unfixable', 'characteristic_missing', 'provider_validation', 'provider_transport', 'rate_limited', 'media_url_not_public', 'readiness_blocked', 'readback_mismatch', 'price_readback_pending', 'media_card_not_ready', 'cancelled', 'weight_not_measured', 'price_quarantine_risk', 'price_quarantined', 'promo_update_rejected', 'promo_update_pending', 'wb_vendor_code_in_trash'])
export const ItemWarningCodeSchema = z.enum(['video_broken', 'video_incompatible', 'image_adapted', 'characteristics_readback_mismatch', 'promo_price_below_minimum', 'wb_vendor_code_recovered'])

export const OperationStateSchema = z.enum(['queued', 'running', 'completed', 'completed_with_failures', 'failed', 'cancelled'])
export const OperationItemStateSchema = z.enum(['pending', 'running', 'ok', 'failed', 'skipped'])
export const OperationListScopeSchema = z.enum(['active', 'all'])

export const OperationCountsSchema = z.object({
  total: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative(),
  running: z.number().int().nonnegative(),
  ok: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  withWarnings: z.number().int().nonnegative(),
}).strict()

// A producer that feeds a calculation states per input how much it supplied; see forge/operations/coverage.py.
export const OperationCoverageSchema = z.object({
  key: SnakeCaseCodeSchema,
  covered: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  percent: z.number().int().min(0).max(100).nullable(),
  reasons: z.record(SnakeCaseCodeSchema, z.number().int().positive()),
}).strict()

export const OperationSchema = z.object({
  id: z.string().uuid(),
  kind: z.string().min(1),
  title: z.string(),
  state: OperationStateSchema,
  actor: z.string(),
  dryRun: z.boolean(),
  createdAt: TimestampSchema.nullable(),
  startedAt: TimestampSchema.nullable(),
  finishedAt: TimestampSchema.nullable(),
  counts: OperationCountsSchema,
  cancellable: z.boolean(),
  // Optional: only producers that feed a calculation report it, and older servers omit it.
  coverage: z.array(OperationCoverageSchema).optional(),
}).strict()

export const OperationItemWarningSchema = z.object({
  code: z.string().min(1),
  message: z.string(),
}).strict()

export const OperationItemSchema = z.object({
  id: z.string().min(1),
  operationId: z.string().uuid(),
  position: z.number().int(),
  subjectKind: z.literal('product'),
  subjectId: z.string().uuid(),
  label: z.string(),
  state: OperationItemStateSchema,
  reasonCode: SnakeCaseCodeSchema.nullable(),
  reason: z.string().nullable(),
  marketplaceIds: z.record(z.string(), z.string()),
  warnings: z.array(OperationItemWarningSchema),
  attempts: z.number().int().nonnegative(),
  updatedAt: TimestampSchema,
  nextAttemptAt: TimestampSchema.nullable(),
  waitingReasonCode: SnakeCaseCodeSchema.nullable(),
  waitingReason: z.string().nullable(),
}).strict()

export const OperationPageSchema = z.object({
  items: z.array(OperationSchema),
  nextCursor: z.string().nullable(),
}).strict()

export const OperationItemPageSchema = z.object({
  items: z.array(OperationItemSchema),
  nextCursor: z.string().nullable(),
}).strict()

export const EnqueueOperationRequestSchema = z.object({
  kind: EnqueueableOperationKindSchema,
  params: z.object({
    batchHash: z.string().length(64),
    dryRun: z.boolean().optional(),
  }).strict(),
}).strict()

export type EnqueueableOperationKind = z.infer<typeof EnqueueableOperationKindSchema>
export type ItemReasonCode = z.infer<typeof ItemReasonCodeSchema>
export type ItemWarningCode = z.infer<typeof ItemWarningCodeSchema>
export type OperationState = z.infer<typeof OperationStateSchema>
export type OperationItemState = z.infer<typeof OperationItemStateSchema>
export type OperationListScope = z.infer<typeof OperationListScopeSchema>
export type OperationCounts = z.infer<typeof OperationCountsSchema>
export type OperationCoverage = z.infer<typeof OperationCoverageSchema>
export type Operation = z.infer<typeof OperationSchema>
export type OperationItemWarning = z.infer<typeof OperationItemWarningSchema>
export type OperationItem = z.infer<typeof OperationItemSchema>
export type OperationPage = z.infer<typeof OperationPageSchema>
export type OperationItemPage = z.infer<typeof OperationItemPageSchema>
export type EnqueueOperationRequest = z.infer<typeof EnqueueOperationRequestSchema>
